import {Helmet} from '@dr.pogodin/react-helmet';
import {openDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {head} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useRefreshSupplierDiscountGroupsMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  refreshDmsDatagrid,
  usePermissions,
} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {BulkEditSupplierDiscountGroups} from './components/BulkEditSupplierDiscountGroups';
import {BULK_EDIT_SUPPLIER_DISCOUNT_GROUP} from './constants/bulkEditSupplierDiscountGroup';
import {WAREHOUSE_SUPPLIER_DISCOUNT_GROUP} from './constants/warehouseSupplierDiscountGroup';

export function WarehousesSupplierDiscountGroups() {
  const navigate = useNavigate();

  const [warehouseDictionaryEdit] = usePermissions({
    permissionKeys: ['warehouseDictionaryEdit'],
  });

  const [refreshSupplierDiscountGroups, {isLoading: isRefreshing}] =
    useRefreshSupplierDiscountGroupsMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId}) => {
    match(actionKey)
      .with('redirectDetail', () => {
        const id = isArray(rowId) ? head(rowId) : rowId;

        navigate(
          composePath(settingsRoutes.warehousesSupplierDiscountGroupsDetail, {params: {id}})
        );
      })
      .with('edit', async () => {
        const ids = isArray(rowId) ? rowId : [rowId];

        await openDialog(
          <BulkEditSupplierDiscountGroups
            supplierDiscountGroupIds={ids}
            data-testid={testIds.settings.warehousesSupplierDiscountGroups('bulkEdit')}
          />,
          {
            id: BULK_EDIT_SUPPLIER_DISCOUNT_GROUP,
            title: i18n.t('entity.warehouse.labels.bulkEditSelectedSupplierDiscounts'),
            size: 'small',
            scrollBehavior: 'outside',
          }
        );
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.supplierDiscounts')} />
      <SettingsTemplate
        header={{
          title: i18n.t('entity.warehouse.labels.supplierDiscounts'),
          actions: [
            {
              isLoading: isRefreshing,
              isDisabled: !warehouseDictionaryEdit,
              tooltip: !warehouseDictionaryEdit
                ? i18n.t('module.warehouse.label.missingWarehouseDictionaryEditPermission')
                : undefined,
              type: 'button',
              title: i18n.t('general.actions.refresh'),
              onClick: () =>
                refreshSupplierDiscountGroups()
                  .unwrap()
                  .then(() => refreshDmsDatagrid(WAREHOUSE_SUPPLIER_DISCOUNT_GROUP))
                  .catch(handleApiError),
              'data-testid': testIds.settings.warehousesSupplierDiscountGroups('actions.refresh'),
            },
          ],
        }}
        data-testid={testIds.settings.warehousesSupplierDiscountGroups('template')}
      >
        <DmsDataGrid
          gridCode={WAREHOUSE_SUPPLIER_DISCOUNT_GROUP}
          actionCallback={actionCallback}
          data-testid={WAREHOUSE_SUPPLIER_DISCOUNT_GROUP}
        />
      </SettingsTemplate>
    </>
  );
}
